import { useLinkSafetyStore } from "~/src/utils/link-safety";
import { create } from "zustand";
import { useGetAvacusAppConfig } from "../hooks/app-config/useGetAvacusAppConfig";
import { type AppConfig } from "../models/app-config";
import { useEffect, useMemo } from "react";
import {
  getAudienceConfig,
  getBasicConfig,
  getContractConfig,
} from "../utils/app-config";
import { numberToHex } from "viem";
import { icWorld } from "~/src/assets/icon";
import { useTranslations } from "next-intl";

const MaxMembersGroupDefault = 10;
const MaxMembersPartyDefault = 5000;
const MaxMultiTransferDefault = 200;
const MaxPinsNFTDefault = 20;
const MaxPinnedRoomsDefault = 3;
const RankingLevelsDefault = {
  gold: 90.0,
  silver: 75.0,
  bronze: 50.0,
};

const configState = {
  basicConfig: {
    disabledChatAddresses: [],
    maxMembersGroup: MaxMembersGroupDefault,
    maxMembersParty: MaxMembersPartyDefault,
    maxMultiTransfer: MaxMultiTransferDefault,
    maxPinsNFT: MaxPinsNFTDefault,
    rankingLevels: RankingLevelsDefault,
    pinnedChainIds: [],
    payToOpenAssets: {},
    supportNetworks: [],
    snsDomain: "",
    partyInvitationURL: "",
    maxPinnedRooms: MaxPinnedRoomsDefault,
    audienceSubscriptionNetworks: null,
    audienceSubscriptionToken: null,
    audienceSubscriptionReferralFee: 0,
    audienceSubscriptionDurationPlans: [],
    enableAudienceSubscription: null,
    maintenance: null,
  },
  contracts: {
    followedNTTContract: {},
    followingNTTContract: {},
    multipleTransferContract: {},
    nftIssueContract: {},
    payToOpenContract: {},
    socialContract: {},
    audienceSubscription: {},
  },
  audience: {
    sendTextMessage: false,
    sendAttachmentMessage: false,
    sendPayment: false,
    sendPaymentRequest: false,
    sendPayToOpen: false,
    reactMessage: false,
    unSendMessage: false,
    deleteMessage: false,
  },
};

interface AppConfigStore extends AppConfig {
  isConfigLoaded: boolean;
  setConfig: (config: Partial<AppConfig>) => void;
}

export const useAppConfigStore = create<AppConfigStore>((set) => ({
  ...configState,
  isConfigLoaded: false,
  setConfig: (config: Partial<AppConfig>) => {
    set((state) => ({ ...state, ...config, isConfigLoaded: true }));
  },
}));

export const useLoadAppConfig = (): void => {
  const { chatConfig } = useGetAvacusAppConfig();
  const setConfig = useAppConfigStore((state) => state.setConfig);

  useEffect(() => {
    if (!chatConfig) return;

    const {
      role_whitelist_actions: roleWhitelistActions,
      contracts: contractsConfig,
    } = chatConfig;

    useLinkSafetyStore.setState({
      trustedHosts: chatConfig.trustedLinkHosts ?? [],
    });
    const basicConfig = getBasicConfig(chatConfig);
    const audience = getAudienceConfig(roleWhitelistActions?.audience);
    const contracts = getContractConfig(contractsConfig);

    setConfig({
      basicConfig,
      contracts,
      audience,
    });
  }, [chatConfig, setConfig]);
};

export interface NetworkType {
  id: string;
  name: string;
  icon: string;
  chain_id: string | number | null;
}

export const useGetSupportChainIds = (): {
  supportChainIds: string[];
  networks: NetworkType[];
} => {
  const t = useTranslations();
  const supportNetworks = useAppConfigStore(
    (state) => state.basicConfig.supportNetworks
  );

  const supportChainIds: string[] = useMemo(
    () => supportNetworks?.map((net) => numberToHex(net.chain_id)),
    [supportNetworks]
  );

  const networks = useMemo(() => {
    const allNetwork = {
      id: "all",
      name: t("all_networks"),
      icon: icWorld,
      chain_id: "all",
    };
    const otherNetworks =
      supportNetworks?.map((item) => ({
        id: item.short_name,
        name: item.name,
        icon: item.image_url,
        chain_id: numberToHex(item?.chain_id),
      })) ?? [];
    return [allNetwork, ...otherNetworks];
  }, [supportNetworks, t]);

  return { supportChainIds, networks };
};
